import { memo } from "react";
import { usePermissions } from "@/rbac/usePermissions";
import type { CanProps, PermissionCheck } from "@/rbac/types/canProps";

export type { CanProps, PermissionCheck };

function evaluateCheck(
  check: PermissionCheck,
  perms: ReturnType<typeof usePermissions>
): boolean {
  if (check.sourceId != null)
    return perms.canSource(check.sourceId, check.moduleKey!, check.action!);
  if (check.stageId != null)
    return perms.canStage(
      check.stageId,
      check.moduleKey!,
      check.action!,
      check.trackId,
      check.subTrackId
    );
  return perms.canGlobal(check.moduleKey!, check.action!);
}

/**
 * Renders children when permission check passes; otherwise fallback (or null).
 * Admins bypass all checks (via usePermissions).
 *
 * Single: <Can moduleKey="lead_management" action="edit">...</Can>
 * Multi:  <Can checks={[...]} match="any">...</Can>
 */
function CanInner(props: CanProps) {
  const perms = usePermissions();
  const { children, fallback = null } = props;

  if (perms.bypassesRbacChecks) return <>{children}</>;

  let allowed = false;
  if (props.checks?.length) {
    const results = props.checks.map((c) => evaluateCheck(c, perms));
    allowed =
      props.match === "all" ? results.every(Boolean) : results.some(Boolean);
  } else if (props.moduleKey && props.action) {
    allowed = evaluateCheck(props as PermissionCheck, perms);
  }

  return <>{allowed ? children : fallback}</>;
}

export const Can = memo(CanInner);
